import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { Button, Card, ErrorBox, Field, inputCls, PageHeader, useAction } from '../components/ui';

const TYPES = ['social', 'institucional', 'produto', 'campanha', 'artigo', 'video', 'evento', 'web', 'abm', 'cultura', 'anuncio', 'outro'];
const CHANNELS = [
  ['linkedin_company', 'LinkedIn institucional'],
  ['linkedin_executive', 'LinkedIn do CEO (só com aprovação dele)'],
  ['instagram_business', 'Instagram (Fase 3)'],
  ['site_blog', 'Site/blog (artigo — A08)'],
  ['email', 'E-mail (Fase 2)'],
  ['interno', 'Comunicação interna (Fase 3)'],
];
const P = 'PENDENTE';

/** Formulário de demanda (§11). Campos críticos aceitam "PENDENTE" e geram perguntas; nada é inventado. */
export function NewRequest() {
  const nav = useNavigate();
  const { me } = useAuth();
  const act = useAction();
  const [f, setF] = useState({
    requesterArea: '',
    internalContact: me?.user.email ?? '',
    type: 'social',
    title: '',
    problem: '',
    businessObjective: '',
    vertical: '',
    persona: '',
    funnelStage: 'topo',
    keyMessage: '',
    valueProposition: '',
    solutions: '',
    vendors: '',
    channels: ['linkedin_company'] as string[],
    cta: '',
    conversionUrl: '',
    refTitle: '',
    refUrl: '',
    partyKind: '',
    partyName: '',
    partyLogo: false,
    partyConsent: '',
    dueDate: '',
    desiredPublishAt: '',
    urgency: 'normal',
    urgencyReason: '',
    budgetApproved: false,
    budgetAmount: '',
    costCenter: '',
    reviewers: ['tecnico'] as string[],
    kpiPrimary: '',
    kpiWindow: '30',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const orP = (v: string) => (v.trim() ? v.trim() : P);
  const list = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      requesterArea: f.requesterArea,
      internalContact: f.internalContact,
      type: f.type,
      title: f.title,
      problem: f.problem,
      businessObjective: f.businessObjective,
      audience: { vertical: orP(f.vertical), persona: orP(f.persona), funnelStage: f.funnelStage || P },
      keyMessage: f.keyMessage,
      valueProposition: orP(f.valueProposition),
      solutions: list(f.solutions),
      vendors: list(f.vendors),
      offers: [],
      channels: f.channels,
      formats: ['post_texto'],
      language: 'pt-BR',
      cta: orP(f.cta),
      conversionUrl: orP(f.conversionUrl),
      references: f.refTitle ? [{ title: f.refTitle, url: f.refUrl || undefined }] : [],
      identifiedParties: f.partyKind ? [{ kind: f.partyKind, name: f.partyName, usesLogoOrImage: f.partyLogo, consentEvidenceId: f.partyConsent || undefined }] : [],
      dueDate: orP(f.dueDate),
      desiredPublishAt: f.desiredPublishAt || undefined,
      urgency: { level: f.urgency, reason: f.urgencyReason || undefined },
      budget: { approved: f.budgetApproved, amountBRL: f.budgetAmount ? Number(f.budgetAmount) : null, costCenter: orP(f.costCenter) },
      reviewersNeeded: f.reviewers,
      kpis: { primary: orP(f.kpiPrimary), secondary: [], measurementWindowDays: Number(f.kpiWindow) || 30 },
    };
    let id = '';
    const ok = await act.run(async () => {
      const res = await api<{ id: string }>('/api/requests', { method: 'POST', body });
      id = res.id;
    });
    if (ok) nav(`/demandas/${id}`);
  };

  const toggle = (k: 'channels' | 'reviewers', v: string) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });

  return (
    <>
      <PageHeader title="Nova demanda" subtitle="Campos em branco marcados como críticos viram PENDENTE e geram perguntas — o sistema não preenche dados sensíveis por suposição." />
      <ErrorBox error={act.error} />
      <form onSubmit={submit} className="space-y-4">
        <Card title="Solicitante">
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Área" required>
              <input className={inputCls} value={f.requesterArea} onChange={set('requesterArea')} required minLength={2} />
            </Field>
            <Field label="Contato interno" required>
              <input className={inputCls} value={f.internalContact} onChange={set('internalContact')} required />
            </Field>
            <Field label="Tipo de demanda" required hint="MVP produz de ponta a ponta: social, institucional, produto, campanha.">
              <select className={inputCls} value={f.type} onChange={set('type')}>
                {TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
          </div>
        </Card>
        <Card title="Problema e objetivo">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Título" required>
              <input className={inputCls} value={f.title} onChange={set('title')} required minLength={5} maxLength={160} />
            </Field>
            <Field label="Objetivo de negócio" required>
              <input className={inputCls} value={f.businessObjective} onChange={set('businessObjective')} required minLength={10} />
            </Field>
            <Field label="Problema" required>
              <textarea className={inputCls} rows={3} value={f.problem} onChange={set('problem')} required minLength={10} />
            </Field>
            <Field label="Mensagem principal" required>
              <textarea className={inputCls} rows={3} value={f.keyMessage} onChange={set('keyMessage')} required minLength={10} />
            </Field>
            <Field label="Proposta de valor" hint="Em branco = PENDENTE (vira hipótese marcada).">
              <input className={inputCls} value={f.valueProposition} onChange={set('valueProposition')} />
            </Field>
          </div>
        </Card>
        <Card title="Público">
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Vertical" hint="Crítico">
              <input className={inputCls} value={f.vertical} onChange={set('vertical')} placeholder="Ex.: Logística/armazéns" />
            </Field>
            <Field label="Persona" hint="Crítico">
              <input className={inputCls} value={f.persona} onChange={set('persona')} placeholder="Ex.: CIO" />
            </Field>
            <Field label="Estágio do funil">
              <select className={inputCls} value={f.funnelStage} onChange={set('funnelStage')}>
                {['topo', 'meio', 'fundo', 'pos_venda'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Soluções (separe por vírgula)">
              <input className={inputCls} value={f.solutions} onChange={set('solutions')} />
            </Field>
            <Field label="Fabricantes envolvidos" hint="Exige validação de parceria vigente.">
              <input className={inputCls} value={f.vendors} onChange={set('vendors')} />
            </Field>
          </div>
        </Card>
        <Card title="Canal, CTA e conversão">
          <fieldset className="mb-3">
            <legend className="text-sm font-medium">Canais</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {CHANNELS.map(([v, l]) => (
                <label key={v} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={f.channels.includes(v)} onChange={() => toggle('channels', v)} /> {l}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="CTA" hint="Crítico">
              <input className={inputCls} value={f.cta} onChange={set('cta')} />
            </Field>
            <Field label="URL de conversão" hint="UTMs são adicionadas automaticamente.">
              <input className={inputCls} type="url" value={f.conversionUrl} onChange={set('conversionUrl')} placeholder="https://www.3snetworks.com.br/#contato" />
            </Field>
          </div>
        </Card>
        <Card title="Referências e terceiros">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Referência (título)">
              <input className={inputCls} value={f.refTitle} onChange={set('refTitle')} />
            </Field>
            <Field label="Referência (URL)" hint="Apenas fontes aceitas (allowlist) contam como prova.">
              <input className={inputCls} type="url" value={f.refUrl} onChange={set('refUrl')} />
            </Field>
            <Field label="Terceiro identificado">
              <select className={inputCls} value={f.partyKind} onChange={set('partyKind')}>
                <option value="">Nenhum</option>
                <option value="cliente">Cliente</option>
                <option value="colaborador">Colaborador</option>
                <option value="parceiro">Parceiro/fabricante</option>
                <option value="terceiro">Outro terceiro</option>
              </select>
            </Field>
            {f.partyKind && (
              <>
                <Field label="Nome do terceiro" required>
                  <input className={inputCls} value={f.partyName} onChange={set('partyName')} required />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.partyLogo} onChange={(e) => setF({ ...f, partyLogo: e.target.checked })} /> Usa logo ou imagem
                </label>
                <Field label="ID da evidência de consentimento/liberação" hint="Sem evidência, o A20 bloqueia a peça.">
                  <input className={inputCls} value={f.partyConsent} onChange={set('partyConsent')} />
                </Field>
              </>
            )}
          </div>
        </Card>
        <Card title="Prazos, urgência, orçamento e KPIs">
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Prazo">
              <input className={inputCls} type="date" value={f.dueDate} onChange={set('dueDate')} />
            </Field>
            <Field label="Publicação desejada (horário de São Paulo)" hint="Sem horário, o sistema propõe ter/qua/qui 09:00.">
              <input className={inputCls} type="datetime-local" value={f.desiredPublishAt} onChange={set('desiredPublishAt')} />
            </Field>
            <Field label="Urgência">
              <select className={inputCls} value={f.urgency} onChange={set('urgency')}>
                {['baixa', 'normal', 'alta', 'critica'].map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
            </Field>
            {f.urgency !== 'normal' && f.urgency !== 'baixa' && (
              <Field label="Motivo da urgência" required>
                <input className={inputCls} value={f.urgencyReason} onChange={set('urgencyReason')} required />
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.budgetApproved} onChange={(e) => setF({ ...f, budgetApproved: e.target.checked })} /> Orçamento aprovado
            </label>
            <Field label="Valor (R$)" hint="Qualquer verba eleva o risco para R2+.">
              <input className={inputCls} type="number" min="0" value={f.budgetAmount} onChange={set('budgetAmount')} />
            </Field>
            <Field label="Centro de custo">
              <input className={inputCls} value={f.costCenter} onChange={set('costCenter')} />
            </Field>
            <Field label="KPI primário" hint="Crítico">
              <input className={inputCls} value={f.kpiPrimary} onChange={set('kpiPrimary')} placeholder="Ex.: reuniões de diagnóstico" />
            </Field>
            <Field label="Janela de medição (dias)">
              <input className={inputCls} type="number" min="1" value={f.kpiWindow} onChange={set('kpiWindow')} />
            </Field>
          </div>
          <fieldset className="mt-3">
            <legend className="text-sm font-medium">Revisores necessários</legend>
            <div className="mt-1 flex gap-3">
              {['tecnico', 'comercial', 'juridico'].map((r) => (
                <label key={r} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={f.reviewers.includes(r)} onChange={() => toggle('reviewers', r)} /> {r}
                </label>
              ))}
            </div>
          </fieldset>
        </Card>
        <Button type="submit" disabled={act.busy || !f.channels.length}>
          {act.busy ? 'Enviando…' : 'Enviar demanda'}
        </Button>
      </form>
    </>
  );
}

/** Complemento de demanda em NEEDS_INFO. */
export function NeedsInfoForm({ requestId, missing, onDone }: { requestId: string; missing: { path: string; question: string }[]; onDone: () => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const act = useAction();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const patch: Record<string, any> = {};
    for (const [path, v] of Object.entries(values)) {
      if (!v.trim()) continue;
      const parts = path.split('.');
      let cur = patch;
      parts.slice(0, -1).forEach((p) => (cur = cur[p] ??= {}));
      cur[parts[parts.length - 1]] = v.trim();
    }
    if (await act.run(() => api(`/api/requests/${requestId}/info`, { method: 'PATCH', body: patch }), 'Informações enviadas.')) onDone();
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <ErrorBox error={act.error} />
      {missing.map((m) => (
        <Field key={m.path} label={m.question} required>
          <input className={inputCls} value={values[m.path] ?? ''} onChange={(e) => setValues({ ...values, [m.path]: e.target.value })} required />
        </Field>
      ))}
      <Button type="submit" disabled={act.busy}>
        Enviar informações
      </Button>
    </form>
  );
}
